import { TabSidebar } from '@/components/custom/TabSidebar'
import { characterService } from '@/features/characters/service/character.service'
import { SceneCard } from '@/features/shared/components/SceneCard'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { STORY_OUTLINE_MIN_WORDS } from '@/features/story/config/story-content.config'
import { useStoryQuery } from '@/features/story/queries/story.queries'
import { getContentStats, hasMinimalStoryContent } from '@/features/story/utils/contentUtils'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { cn } from '@/shared/lib/utils'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { $getNodeByKey } from 'lexical'
import { Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSpEditorStore } from '../stores/sp-editor.store'
import { SceneNode } from './editor/nodes/SceneNode'
import { useSpEditorProvider } from './editor/SpEditorProvider'

function CharacterCard({
    index,
    name,
    href,
    className,
}: {
    index: number
    name: string
    href: string
    className?: string
}) {
    const classes = cn(
        'group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
        'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
        className
    )
    return (
        <Link to={href} className={classes}>
            <span className="shrink-0 text-sidebar-foreground/50 tabular-nums">{index}.</span>
            <span className="truncate" title={name}>
                {name}
            </span>
        </Link>
    )
}

export default function CharacterLocationTabbar() {
    const { addCharacter, characters } = useSpEditorProvider()
    const { scenes, editor } = useSceneStore()
    const { project } = useWorkspace()
    const setIsChoiceDialogOpen = useSpEditorStore((state) => state.setIsChoiceDialogOpen)
    const { data: storyEntity } = useStoryQuery(project?.id, 'master')
    const [inputValue, setInputValue] = useState('')

    const hasMinimalContent = storyEntity?.content
        ? hasMinimalStoryContent(storyEntity.content)
        : false
    const contentStats = storyEntity?.content
        ? getContentStats(storyEntity.content)
        : { wordCount: 0, characterCount: 0, nodeCount: 0 }

    const helperText = !storyEntity
        ? `Write at least ${STORY_OUTLINE_MIN_WORDS} words in the story editor to enable screenplay outline generation.`
        : hasMinimalContent
          ? null
          : `Needs at least ${STORY_OUTLINE_MIN_WORDS} words in story. Current story length: ${contentStats.wordCount}/${STORY_OUTLINE_MIN_WORDS} words.`

    const getButtonHint = () => {
        if (!storyEntity) {
            return 'No story found to generate from'
        }
        if (!hasMinimalContent) {
            const wordsNeeded = Math.max(0, STORY_OUTLINE_MIN_WORDS - contentStats.wordCount)
            return `Add at least ${wordsNeeded} more words in the story editor to generate screenplay outline (${contentStats.wordCount}/${STORY_OUTLINE_MIN_WORDS} words)`
        }
        return 'Generate screenplay outline from your story'
    }

    const handleAdd = () => {
        const trimmed = inputValue.trim()
        if (!trimmed) return
        const character = characterService.buildCharacter({
            projectId: project?.id ?? '',
            fullname: trimmed,
        })
        addCharacter(character)
        setInputValue('')
    }

    return (
        <TabSidebar
            tabs={[
                {
                    value: 'scene-map',
                    label: 'Scene',
                    content: (
                        <div className="flex flex-col flex-1 min-h-0">
                            <ScrollArea className="flex-1 min-h-0 rounded-md">
                                <div className="flex flex-col gap-0.5">
                                    {scenes.map((element, index) => {
                                        const nodeKey = element.getKey()
                                        const text = editor?.getEditorState().read(() => {
                                            const latestNode = $getNodeByKey(nodeKey)
                                            return latestNode
                                                ? getSceneHeader(latestNode as SceneNode)
                                                : null
                                        })
                                        if (text === null) return null
                                        return (
                                            <SceneCard
                                                key={nodeKey}
                                                index={index + 1}
                                                title={text ?? undefined}
                                                href={`#scene-${element.id}`}
                                            />
                                        )
                                    })}
                                    {scenes.length === 0 && (
                                        <p className="text-xs text-muted-foreground text-center py-4">
                                            No scenes yet
                                        </p>
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    ),
                },
                {
                    value: 'characters',
                    label: 'Characters',
                    content: (
                        <div className="flex flex-col flex-1 min-h-0 gap-2">
                            {/* Add character input */}
                            <div className="relative shrink-0">
                                <Input
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAdd()
                                    }}
                                    placeholder="Add character…"
                                    className="pr-8 text-sm h-8"
                                />
                                <button
                                    onClick={handleAdd}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    aria-label="Add character"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>

                            {/* Character list */}
                            <ScrollArea className="flex-1 min-h-0 rounded-md">
                                <div className="flex flex-col gap-0.5">
                                    {characters.length === 0 ? (
                                        <p className="text-xs text-muted-foreground text-center py-4">
                                            No characters yet
                                        </p>
                                    ) : (
                                        [...characters]
                                            .sort((a, b) => {
                                                const aTime = a.created_at
                                                    ? new Date(a.created_at).getTime()
                                                    : 0
                                                const bTime = b.created_at
                                                    ? new Date(b.created_at).getTime()
                                                    : 0
                                                return aTime - bTime
                                            })
                                            .map((c, index) => (
                                                <CharacterCard
                                                    key={c.id || index}
                                                    index={index + 1}
                                                    name={c.fullname ?? (c as any).name ?? '—'}
                                                    href={`/workspace/${project?.id}/characters/${c.id}`}
                                                />
                                            ))
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    ),
                },
                {
                    value: 'more',
                    label: 'More',
                    content: (
                        <div className="flex flex-col flex-1 min-h-0">
                            <div className="rounded-lg border border-border/60 bg-background/80 p-3">
                                <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    AI Actions
                                </p>
                                <Button
                                    variant="outline"
                                    className={`w-full justify-start gap-2 border-dashed transition-colors ${
                                        hasMinimalContent
                                            ? 'border-primary/50 hover:border-primary hover:bg-primary/5'
                                            : 'border-muted-foreground/30 text-muted-foreground'
                                    }`}
                                    onClick={() => setIsChoiceDialogOpen(true)}
                                    disabled={!hasMinimalContent}
                                    title={getButtonHint()}
                                >
                                    <Sparkles className="h-4 w-4 text-primary" />
                                    <span>Generate Screenplay Outline</span>
                                </Button>
                                {helperText && (
                                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                                        {helperText}
                                    </p>
                                )}
                            </div>
                        </div>
                    ),
                },
            ]}
            onEdit={() => {}}
            onReset={() => {}}
        />
    )
}
